import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCog, CalendarCheck, BookOpen, Filter, ChevronRight, Search, X, UserCheck, Clock, AlertTriangle, ClipboardCheck, Activity, Plus, TrendingUp, Megaphone, Send, BarChart3, Brain, Database, UserPlus, Sparkles, FileStack, Building2, IndianRupee, Crown, Receipt } from 'lucide-react';
import { RegisteredUsersDialog } from '../components/super-admin/RegisteredUsersDialog';
import NewsletterSubscribersDialog from '../components/super-admin/NewsletterSubscribersDialog';
import { WorkspaceHero } from '../components/generator/WorkspaceHero';
import { navIconMap } from '../components/layout/Sidebar';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { PieChartCard, BarChartCard } from '../components/common/Charts';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { useApp } from '../contexts/AppContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../services/api';

export function DashboardPage() {
  const { currentUser, filteredNavigation } = useApp();
  const heroActions = (filteredNavigation || [])
    .filter((n) => n.id !== 'dashboard')
    .slice(0, 3)
    .map((n) => ({ id: n.id, label: n.label, hint: `Open ${n.label.toLowerCase()}`, icon: navIconMap[n.id] || ChevronRight, onClick: () => navigate(n.path) }));
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attPeriod, setAttPeriod] = useState('month');
  const [attPercentage, setAttPercentage] = useState(null);

  // Teacher-specific state
  const [teacherDialog, setTeacherDialog] = useState(false);
  const [teacherFilter, setTeacherFilter] = useState('all');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [subsDialog, setSubsDialog] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isTeacher = currentUser?.role === 'teacher';
  const isIndividual = !currentUser?.schoolId;
  // Iter 77b — tier from BOSCO /access API (truth source = ce_subscriptions).
  const [boscoAccess, setBoscoAccess] = useState({ elite: false, tier: 'free', studioAvailable: false });
  useEffect(() => {
    if (!isIndividual || !currentUser) return;
    const token = localStorage.getItem('sip_token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bosco/access`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setBoscoAccess(d))
      .catch(() => {});
  }, [currentUser?.userId, isIndividual]);
  const isElite = boscoAccess.studioAvailable;
  const tier = boscoAccess.tier || 'free';

  // Broadcast state
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ title: '', message: '', targetType: 'school', priority: 'normal' });
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => localStorage.getItem('sip_token') || '';

  // ─── Super-Admin real-time platform metrics ───
  // Polled every 30 s while the tab is visible. Cancelled when the role
  // isn't super_admin or the user navigates away.
  const [platformMetrics, setPlatformMetrics] = useState(null);
  const [registeredUsersOpen, setRegisteredUsersOpen] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let cancelled = false;
    let timer = null;
    const fetchMetrics = async () => {
      try {
        const data = await fetch(`${API_URL}/api/super-admin/platform-metrics`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }).then((r) => (r.ok ? r.json() : null));
        if (!cancelled && data) setPlatformMetrics(data);
      } catch { /* silent */ }
    };
    fetchMetrics();
    timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchMetrics();
    }, 30000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [isSuperAdmin, API_URL]);

  // Pretty "X mins ago" for the Last Login card.
  const fmtRelative = (iso) => {
    if (!iso) return '—';
    try {
      const then = new Date(iso).getTime();
      const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      const m = Math.floor(diffSec / 60);
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}h ago`;
      const d = Math.floor(h / 24);
      return `${d}d ago`;
    } catch { return '—'; }
  };

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { if (isAdmin) { loadAttendancePercentage(); loadBroadcasts(); } }, [attPeriod]);

  const loadBroadcasts = async () => {
    try {
      const data = await fetch(`${API_URL}/api/broadcasts`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json());
      setBroadcasts(data.broadcasts?.slice(0, 5) || []);
    } catch {}
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.title || !broadcastForm.message) return;
    setBroadcastSending(true);
    try {
      await fetch(`${API_URL}/api/broadcasts`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(broadcastForm) });
      setBroadcastOpen(false);
      setBroadcastForm({ title: '', message: '', targetType: 'school', priority: 'normal' });
      loadBroadcasts();
    } catch {}
    setBroadcastSending(false);
  };

  const loadDashboard = async () => {
    setIsLoading(true);
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (e) {
      console.error('Failed to load dashboard:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAttendancePercentage = async () => {
    try {
      const data = await api.getAttendancePercentage(attPeriod);
      setAttPercentage(data);
    } catch (e) { console.error(e); }
  };

  // Fee-related state is admin-only

  if (isLoading) {
    return (
      <div data-testid="dashboard-page" className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Filter teachers for teacher dialog
  const filteredTeachers = (stats?.teachersList || []).filter(t => {
    const matchFilter = teacherFilter === 'all' || t.status === teacherFilter;
    const matchSearch = !teacherSearch || t.name?.toLowerCase().includes(teacherSearch.toLowerCase());
    return matchFilter && matchSearch;
  });

  // Chart data
  const classDistribution = Array.isArray(stats?.classDistribution) ? stats.classDistribution : [];

  // ========== TEACHER DASHBOARD ==========
  if (isTeacher) {
    return (
      <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
        <WorkspaceHero
          name={currentUser?.name}
          eyebrow="Teacher Dashboard"
          description="Your classes, students and schedule at a glance — jump straight into what matters today."
          quickActions={heroActions}
        />
        <h1 className="sr-only">Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <TooltipProvider>
            {/* Total Students → Students page */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/students')} data-testid="card-total-students">
                  <StatsCard title="Total Students" value={stats?.assignedStudents || 0} description="Students assigned to your classes" icon={Users} delay={0} className="h-full" />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>View the complete students list</p></TooltipContent>
            </Tooltip>

            {/* Total Teachers → Dialog with filters */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => setTeacherDialog(true)} data-testid="card-total-teachers">
                  <StatsCard title="Total Teachers" value={stats?.totalTeachers || 0} description={`${stats?.availableCount || 0} available, ${stats?.onLeaveCount || 0} on leave`} icon={UserCog} delay={0.05} className="h-full" />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>View all teachers with availability status</p></TooltipContent>
            </Tooltip>

            {/* Teacher's Own Attendance YTD */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/leave-management')} data-testid="card-own-attendance">
                  <StatsCard title="My Attendance (YTD)" value={`${stats?.teacherOwnAttendanceYTD ?? 100}%`} description={`${stats?.teacherOwnLeaveDays || 0} leave day${stats?.teacherOwnLeaveDays !== 1 ? 's' : ''} / ${stats?.teacherOwnWorkingDays || 0} working days`} icon={CalendarCheck} delay={0.1} className="h-full" />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>View your leave history and attendance details</p></TooltipContent>
            </Tooltip>

            {/* Classes Assigned → live data */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/teacher-schedule')} data-testid="card-classes-assigned">
                  <StatsCard title="Classes Assigned" value={stats?.assignedClassesCount || 0} description={`${(stats?.assignedClasses || []).join(', ') || 'None'}`} icon={BookOpen} delay={0.15} className="h-full" />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>View your class schedule and timetable</p></TooltipContent>
            </Tooltip>

            {/* Substitutes Assigned → clickable to show list */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => setSubsDialog(true)} data-testid="card-substitutes-assigned">
                  <StatsCard title="Substitutes Assigned" value={stats?.mySubstitutesCount || 0} description="Teachers covering your classes" icon={UserCheck} delay={0.2} className="h-full" />
                </div>
              </TooltipTrigger>
              <TooltipContent><p>View substitute teacher assignments</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Teacher List Dialog */}
        <Dialog open={teacherDialog} onOpenChange={setTeacherDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5" /> Teachers</DialogTitle>
              <DialogDescription>{stats?.totalTeachers || 0} total teachers</DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 mt-2">
              <Select value={teacherFilter} onValueChange={setTeacherFilter}>
                <SelectTrigger className="w-[150px]" data-testid="teacher-filter-select">
                  <Filter className="w-3.5 h-3.5 mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({stats?.totalTeachers || 0})</SelectItem>
                  <SelectItem value="available">Available ({stats?.availableCount || 0})</SelectItem>
                  <SelectItem value="on_leave">On Leave ({stats?.onLeaveCount || 0})</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search..." value={teacherSearch} onChange={e => setTeacherSearch(e.target.value)} className="pl-8 h-9" data-testid="teacher-dialog-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto mt-3 space-y-2 min-h-0">
              {filteredTeachers.length === 0 ? (
                <p className="text-center py-8 text-sm text-muted-foreground">No teachers match filters.</p>
              ) : (
                filteredTeachers.map(t => (
                  <div key={t.teacherId} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" onClick={() => { setTeacherDialog(false); navigate(`/teachers/${t.teacherId}`); }} data-testid={`teacher-item-${t.teacherId}`}>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9">
                        <AvatarFallback className="bg-brand-deep-blue text-white text-xs font-semibold">
                          {t.name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.subject || 'General'}{t.assignedClasses?.length > 0 ? ` · ${t.assignedClasses.join(', ')}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={t.status === 'available' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}>
                        {t.status === 'available' ? 'Available' : t.leaveType || 'On Leave'}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Substitutes Assigned Dialog */}
        <Dialog open={subsDialog} onOpenChange={setSubsDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5" /> Substitutes Assigned</DialogTitle>
              <DialogDescription>Teachers covering your classes while on leave</DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-3 space-y-2 min-h-0">
              {(stats?.mySubstitutes || []).length === 0 ? (
                <p className="text-center py-8 text-sm text-muted-foreground">No substitutes assigned currently.</p>
              ) : (
                (stats?.mySubstitutes || []).map((sub, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`sub-item-${i}`}>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9">
                        <AvatarFallback className="bg-brand-deep-blue text-white text-xs font-semibold">
                        {(sub.substituteTeacherName || '?').split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{sub.substituteTeacherName || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{sub.classSection} · Period {sub.periodNumber}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-brand-deep-blue/10 text-brand-deep-blue">{sub.date}</Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== ADMIN DASHBOARD ==========
  const staffAtt = stats?.staffAttendance;
  const pending = stats?.pendingApprovals;
  const trend = stats?.attendanceTrend || [];
  const recentLogs = stats?.recentActivity || [];

  const formatAction = (action) => {
    return (action || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).substring(0, 40);
  };
  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      <WorkspaceHero
        name={currentUser?.name}
        eyebrow={isSuperAdmin ? 'Platform Control Center' : 'School Command Center'}
        description={isSuperAdmin
          ? 'Schools, subscriptions, AI traces and platform analytics — everything across ActanonEdu in one view.'
          : 'Attendance, approvals, staff and activity across your school — all in one calm, focused view.'}
        quickActions={heroActions}
      />
      <h1 className="sr-only">Dashboard</h1>

      {/* BOSCO Studio™ entry — Elite Individual gets the live card, others see "Upgrade" */}
      {isIndividual && (
        <button
          onClick={() => navigate(isElite ? '/bosco-studio' : '/pricing')}
          data-testid={isElite ? 'card-bosco-studio' : 'card-bosco-studio-locked'}
          className={`group w-full text-left rounded-xl border p-5 transition-all hover:shadow-lg hover:scale-[1.005] ${
            isElite
              ? 'border-emerald-500/30 bg-gradient-to-br from-slate-950 via-black to-emerald-950/40 text-slate-100'
              : 'border-amber-200 dark:border-amber-900/40 bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-amber-900/20 dark:via-slate-900 dark:to-orange-900/20'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center font-mono font-bold text-lg ${
              isElite ? 'bg-emerald-500 text-black' : 'bg-slate-200 text-slate-500'
            }`}>
              ❯_
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`text-base font-bold ${isElite ? 'text-emerald-100' : ''}`}>
                  BOSCO Studio™ <span className="text-xs font-normal opacity-70">Expert Mode</span>
                </h3>
                <Badge variant="outline" className={isElite
                  ? 'text-[10px] bg-emerald-900/30 text-emerald-300 border-emerald-700'
                  : 'text-[10px] bg-amber-100 text-amber-800 border-amber-300'}>
                  {isElite ? 'ELITE' : 'UPGRADE TO ELITE'}
                </Badge>
              </div>
              <p className={`text-xs mt-1 ${isElite ? 'text-slate-400' : 'text-muted-foreground'}`}>
                {isElite
                  ? 'Command-line academic OS · 217 BOSCO Language™ commands · Custom scripts · Multilingual'
                  : 'Unlock the command-line academic OS — 217 native commands, custom workflows, and multilingual generation.'}
              </p>
            </div>
            <ChevronRight className={`h-5 w-5 group-hover:translate-x-1 transition-transform ${isElite ? 'text-emerald-400' : 'text-amber-600'}`} />
          </div>
        </button>
      )}

      {/* Super Admin only — Intelligence + AI Debug + Knowledge Hub + Analytics entry cards */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="super-admin-cards">
          <button
            onClick={() => navigate('/super-admin/intelligence')}
            data-testid="card-performance-analytics"
            className="group text-left ae-surface p-5 hover:border-ae-blue/40 hover:shadow-soft-lg transition-[border-color,box-shadow] duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105">
                <Brain className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">Performance Analytics</h3>
                  <Badge variant="outline" className="text-[10px] bg-ae-blue/10 text-ae-blue border-ae-blue/20">SUPER ADMIN</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Multi-school intelligence, benchmarking & AI-driven product insights.</p>
              </div>
              <ChevronRight className="h-5 w-5 text-ae-blue group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => navigate('/super-admin/ai-debug-logs')}
            data-testid="card-ai-debug-logs"
            className="group text-left ae-surface p-5 hover:border-ae-blue/40 hover:shadow-soft-lg transition-[border-color,box-shadow] duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105">
                <Database className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">AI Debug Trace Viewer</h3>
                  <Badge variant="outline" className="text-[10px] bg-ae-blue/10 text-ae-blue border-ae-blue/20">SUPER ADMIN</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Section A engine traces — inspect every generation step end-to-end.</p>
              </div>
              <ChevronRight className="h-5 w-5 text-ae-blue group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => navigate('/super-admin/blog')}
            data-testid="card-knowledge-hub-cms"
            className="group text-left ae-surface p-5 hover:border-ae-blue/40 hover:shadow-soft-lg transition-[border-color,box-shadow] duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105">
                <BookOpen className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">Knowledge Hub CMS</h3>
                  <Badge variant="outline" className="text-[10px] bg-ae-blue/10 text-ae-blue border-ae-blue/20">SUPER ADMIN</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Author articles, manage categories, and moderate comments (Bosco Guardian).</p>
              </div>
              <ChevronRight className="h-5 w-5 text-ae-blue group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => navigate('/super-admin/gst-revenue')}
            data-testid="card-gst-revenue"
            className="group text-left ae-surface p-5 hover:border-ae-blue/40 hover:shadow-soft-lg transition-[border-color,box-shadow] duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105">
                <Receipt className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">GST &amp; Revenue</h3>
                  <Badge variant="outline" className="text-[10px] bg-ae-blue/10 text-ae-blue border-ae-blue/20">SUPER ADMIN</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Monthly GST collected — CGST, SGST, IGST split with CSV export for filing.</p>
              </div>
              <ChevronRight className="h-5 w-5 text-ae-blue group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={() => navigate('/super-admin/analytics')}
            data-testid="card-analytics"
            className="group text-left ae-surface p-5 hover:border-ae-blue/40 hover:shadow-soft-lg transition-[border-color,box-shadow] duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-ae-navy text-ae-cyan flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105">
                <Activity className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold">Analytics</h3>
                  <Badge variant="outline" className="text-[10px] bg-ae-blue/10 text-ae-blue border-ae-blue/20">SUPER ADMIN</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Visitor behaviour, top events, device split, and Clarity deep-links.</p>
              </div>
              <ChevronRight className="h-5 w-5 text-ae-blue group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      )}

      {/* ─── Super Admin only — 8 platform metrics (real-time, polled every 30s) ─── */}
      {isSuperAdmin && (
        <div className="space-y-3" data-testid="super-admin-platform-metrics">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Platform Pulse</h2>
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              LIVE
            </Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setRegisteredUsersOpen(true)}
              data-testid="metric-total-users-trigger"
              className="text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-deep-blue focus-visible:ring-offset-2 transition-transform hover:-translate-y-0.5 hover:shadow-lg"
              aria-label="View all registered users"
            >
              <StatsCard
                data-testid="metric-total-users"
                title="Total Registered Users"
                value={platformMetrics?.totalRegisteredUsers ?? '—'}
                description="Click to view roster →"
                icon={Users}
                delay={0}
                className="h-full cursor-pointer"
              />
            </button>
            <StatsCard
              data-testid="metric-signups-today"
              title="Signups Today"
              value={platformMetrics?.signupsToday ?? '—'}
              description="Since midnight UTC"
              icon={UserPlus}
              delay={0.03}
              className="h-full"
            />
            <StatsCard
              data-testid="metric-dau"
              title="Daily Active Users"
              value={platformMetrics?.dailyActiveUsers ?? '—'}
              description="Logged in last 24h"
              icon={Activity}
              delay={0.06}
              className="h-full"
            />
            <StatsCard
              data-testid="metric-resources"
              title="Total Resources Generated"
              value={(platformMetrics?.totalResourcesGenerated ?? 0).toLocaleString('en-IN')}
              description="Lifetime generations"
              icon={FileStack}
              delay={0.09}
              className="h-full"
            />
            <StatsCard
              data-testid="metric-last-login"
              title="Last Login Time"
              value={fmtRelative(platformMetrics?.lastLoginAt)}
              description={platformMetrics?.lastLoginUser?.name ? `by ${platformMetrics.lastLoginUser.name}` : 'No logins yet'}
              icon={Clock}
              delay={0.12}
              className="h-full"
            />
            <StatsCard
              data-testid="metric-schools"
              title="Schools Registered"
              value={platformMetrics?.schoolsRegistered ?? '—'}
              description="Active institutions"
              icon={Building2}
              delay={0.15}
              className="h-full"
            />
            <StatsCard
              data-testid="metric-free-vs-premium"
              title="Free vs Premium Users"
              value={
                platformMetrics
                  ? `${platformMetrics.freeUsers} / ${platformMetrics.premiumUsers}`
                  : '—'
              }
              description={
                platformMetrics
                  ? `${Math.round(((platformMetrics.premiumUsers || 0) / Math.max(1, (platformMetrics.totalRegisteredUsers || 1))) * 100)}% premium`
                  : 'Free / Premium split'
              }
              icon={Crown}
              delay={0.18}
              className="h-full"
            />
            <StatsCard
              data-testid="metric-mrr"
              title="Revenue (MRR)"
              value={`₹${(platformMetrics?.mrrInr ?? 0).toLocaleString('en-IN')}`}
              description="Monthly recurring revenue"
              icon={IndianRupee}
              delay={0.21}
              className="h-full"
            />
          </div>
          <RegisteredUsersDialog open={registeredUsersOpen} onOpenChange={setRegisteredUsersOpen} />
          <NewsletterSubscribersDialog open={newsletterOpen} onOpenChange={setNewsletterOpen} />
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setNewsletterOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md bg-indigo-600/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-600/20 transition-colors"
              data-testid="open-newsletter-subscribers"
            >
              📧 View Newsletter Subscribers
            </button>
          </div>
        </div>
      )}

      {/* Row 1: Key Stats (5 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/students')} data-testid="card-total-students">
                <StatsCard title="Total Students" value={stats?.totalStudents || 0} description={`${stats?.archivedStudents || 0} archived`} icon={Users} delay={0} className="h-full" />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>Click to view and manage the complete student list</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/teachers')} data-testid="card-total-staff">
                <StatsCard title="Total Staff" value={staffAtt?.totalStaff || stats?.totalTeachers || 0} description={`${staffAtt?.presentToday || 0} present, ${staffAtt?.onLeaveToday || 0} on leave`} icon={UserCog} delay={0.05} className="h-full" />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>Click to view all staff and their profiles</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/attendance')} data-testid="card-student-attendance">
                <StatsCard
                  title="Student Attendance"
                  value={attPercentage ? `${attPercentage.percentage}%` : `${stats?.todayAttendance?.percentage || 0}%`}
                  description="Current period rate"
                  icon={CalendarCheck}
                  delay={0.1}
                  className="h-full"
                  filterSlot={
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select value={attPeriod} onValueChange={setAttPeriod}>
                        <SelectTrigger className="h-7 text-xs w-[110px]" data-testid="att-period-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="today">Today</SelectItem>
                          <SelectItem value="week">Week</SelectItem>
                          <SelectItem value="month">Month</SelectItem>
                          <SelectItem value="quarterly">Quarter</SelectItem>
                          <SelectItem value="yearly">Year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  }
                />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>Click to view detailed student attendance records</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/teachers')} data-testid="card-staff-attendance">
                <StatsCard title="Staff Attendance" value={`${staffAtt?.percentage || 0}%`} description={`${staffAtt?.onLeaveToday || 0} on leave today`} icon={UserCheck} delay={0.15} className="h-full" />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>Click to view staff availability and leave status</p></TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer transition-transform hover:scale-[1.02]" onClick={() => navigate('/leave-management')} data-testid="card-pending-approvals">
                <StatsCard title="Pending Approvals" value={pending?.total || 0} description={`${pending?.leaves || 0} leave requests`} icon={ClipboardCheck} delay={0.2} className="h-full" />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>Click to review and approve pending requests</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Row 2: Quick Actions */}
      <div className="flex flex-wrap gap-2" data-testid="quick-actions">
        <Button size="sm" variant="outline" onClick={() => navigate('/students')} data-testid="qa-add-student">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Student
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate('/teachers')} data-testid="qa-add-teacher">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Teacher
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate('/attendance')} data-testid="qa-attendance">
          <CalendarCheck className="w-3.5 h-3.5 mr-1" /> Mark Attendance
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate('/leave-management')} data-testid="qa-leaves">
          <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Leave Approvals
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate('/class-assignments')} data-testid="qa-classes">
          <BookOpen className="w-3.5 h-3.5 mr-1" /> Class Assignments
        </Button>
        <Button size="sm" variant="outline" onClick={() => setBroadcastOpen(true)} data-testid="qa-broadcast">
          <Megaphone className="w-3.5 h-3.5 mr-1" /> Broadcast
        </Button>
      </div>

      {/* Row 3: Charts */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DataCard title="Attendance Trend (7 Days)" delay={0.2}>
            {trend.length > 0 ? (
              <BarChartCard data={trend} xKey="day" yKey="rate" />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No trend data</div>
            )}
          </DataCard>
          <DataCard title="Students by Class" delay={0.25}>
            {classDistribution.length > 0 ? (
              <PieChartCard data={classDistribution} nameKey="name" valueKey="value" />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data</div>
            )}
          </DataCard>
        </div>
      )}

      {/* Row 4: Announcements Feed */}
      {isAdmin && broadcasts.length > 0 && (
        <DataCard title="Recent Announcements" delay={0.28}>
          <div className="space-y-2 max-h-[200px] overflow-y-auto" data-testid="announcements-feed">
            {broadcasts.map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/30">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${b.priority === 'urgent' ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
                  <Megaphone className={`w-3.5 h-3.5 ${b.priority === 'urgent' ? 'text-red-500' : 'text-blue-500'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{b.title} {b.priority === 'urgent' && <Badge className="bg-red-500/10 text-red-600 text-[10px] ml-1">URGENT</Badge>}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{b.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">By {b.createdByName} · {b.readBy?.length || 0} read · {b.createdAt?.substring(0, 10)}</p>
                </div>
              </div>
            ))}
          </div>
        </DataCard>
      )}

      {/* Row 5: Recent Activity */}
      {isAdmin && recentLogs.length > 0 && (
        <DataCard title="Recent Activity" delay={0.3}>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="recent-activity-list">
            {recentLogs.map((log, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`activity-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-brand-deep-blue/10 flex items-center justify-center shrink-0">
                    <Activity className="w-3.5 h-3.5 text-brand-deep-blue" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{formatAction(log.action)}</p>
                    <p className="text-xs text-muted-foreground">{log.performerName || 'System'} · {log.role || ''}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 ml-2">{timeAgo(log.createdAt)}</span>
              </div>
            ))}
          </div>
        </DataCard>
      )}

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5" /> Send Broadcast</DialogTitle>
            <DialogDescription>Send announcement to school, class, or role</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title *</Label><Input value={broadcastForm.title} onChange={e => setBroadcastForm({...broadcastForm, title: e.target.value})} placeholder="Announcement title" data-testid="broadcast-title" /></div>
            <div><Label>Message *</Label><Textarea value={broadcastForm.message} onChange={e => setBroadcastForm({...broadcastForm, message: e.target.value})} rows={3} placeholder="Write your message..." data-testid="broadcast-message" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Target</Label>
                <Select value={broadcastForm.targetType} onValueChange={v => setBroadcastForm({...broadcastForm, targetType: v})}>
                  <SelectTrigger data-testid="broadcast-target"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">Entire School</SelectItem>
                    <SelectItem value="role">By Role</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={broadcastForm.priority} onValueChange={v => setBroadcastForm({...broadcastForm, priority: v})}>
                  <SelectTrigger data-testid="broadcast-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button>
            <Button onClick={sendBroadcast} disabled={broadcastSending} className="bg-brand-deep-blue" data-testid="broadcast-send">
              {broadcastSending ? 'Sending...' : <><Send className="w-3.5 h-3.5 mr-1" /> Send</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
